/**
 * ============================================================================
 * USER STORE - ZUSTAND
 * ============================================================================
 * 
 * Global user state management (profile, settings, preferences)
 * 
 * @typedef {Object} UserProfile
 * @typedef {Object} UserPreferences
 * @typedef {Object} UserState
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { validateObject, validateProfile, validateId } from './utils/validators';
import { addItemOptimized, limitArraySize, deduplicateById } from './utils/performance';
import { createSelector } from './utils/selectors';

const useUserStore = create(
  persist(
    (set, get) => ({
      // State
      profile: null,
      stats: null,
      activity: [],
      transactions: [],
      ratings: [],
      givenRatings: [],
      settings: null,
      verificationStatus: null,
      preferences: {
        notifications: {
          email: true,
          sms: false,
          push: true,
        },
        privacy: {
          profileVisibility: 'public',
          showEmail: false,
          showPhone: false,
        },
        language: 'en',
        currency: 'XAF',
      },
      loading: false,
      error: null,

      // Actions
      /**
       * Set profile with validation
       * @param {UserProfile} profile - User profile
       */
      setProfile: (profile) => {
        if (profile !== null) {
          validateProfile(profile);
        }
        set({ profile });
      },

      /**
       * Update profile with validation and optimistic update support
       * @param {Object} updates - Profile updates
       * @param {boolean} optimistic - Whether this is an optimistic update
       */
      updateProfile: (updates, optimistic = false) => {
        validateObject(updates, 'Updates');
        if (optimistic) {
          // Optimistic update - apply immediately
          set((state) => ({
            profile: state.profile ? { ...state.profile, ...updates } : updates,
          }));
        } else {
          // Regular update
          set((state) => ({
            profile: state.profile ? { ...state.profile, ...updates } : updates,
          }));
        }
      },

      setStats: (stats) => {
        set({ stats });
      },

      /**
       * Set activity with validation and size limit
       * @param {Array} activity - Activity array
       */
      setActivity: (activity) => {
        if (!Array.isArray(activity)) {
          throw new Error('Activity must be an array');
        }
        const limited = limitArraySize(activity, 500);
        set({ activity: limited });
      },

      /**
       * Add activity item with deduplication and size limit
       * @param {Object} activityItem - Activity item
       */
      addActivity: (activityItem) => {
        validateObject(activityItem, 'Activity item');
        set((state) => ({
          activity: addItemOptimized(state.activity, activityItem, {
            idKey: 'id',
            maxSize: 500,
            prepend: true,
          }),
        }));
      },

      setTransactions: (transactions) => {
        set({ transactions });
      },

      setRatings: (ratings) => {
        set({ ratings });
      },

      /**
       * Add rating with validation and deduplication
       * @param {Object} rating - Rating object
       */
      addRating: (rating) => {
        validateObject(rating, 'Rating');
        if (!rating.id) {
          throw new Error('Rating must have an id');
        }
        set((state) => ({
          ratings: addItemOptimized(state.ratings, rating, {
            idKey: 'id',
            maxSize: 1000,
            prepend: false,
          }),
        }));
      },

      setGivenRatings: (givenRatings) => {
        set({ givenRatings });
      },

      addGivenRating: (rating) => {
        set((state) => ({
          givenRatings: [...state.givenRatings, rating],
        }));
      },

      setSettings: (settings) => {
        set({ settings });
      },

      updateSettings: (updates) => {
        set((state) => ({
          settings: state.settings
            ? { ...state.settings, ...updates }
            : updates,
        }));
      },

      setVerificationStatus: (status) => {
        set({ verificationStatus: status });
      },

      setPreferences: (preferences) => {
        set((state) => ({
          preferences: { ...state.preferences, ...preferences },
        }));
      },

      /**
       * Update preference with validation
       * @param {string} category - Preference category
       * @param {string} key - Preference key
       * @param {*} value - Preference value
       */
      updatePreference: (category, key, value) => {
        if (typeof category !== 'string' || category.trim().length === 0) {
          throw new Error('Category must be a non-empty string');
        }
        if (typeof key !== 'string' || key.trim().length === 0) {
          throw new Error('Key must be a non-empty string');
        }
        set((state) => {
          // Top-level preference keys (language, currency) used by I18nProvider
          if (category === 'preferences' && (key === 'language' || key === 'currency')) {
            return {
              preferences: {
                ...state.preferences,
                [key]: value,
              },
            };
          }
          return {
            preferences: {
              ...state.preferences,
              [category]: {
                ...state.preferences[category],
                [key]: value,
              },
            },
          };
        });
      },

      setLoading: (loading) => {
        set({ loading });
      },

      setError: (error) => {
        set({ error });
      },

      clearError: () => {
        set({ error: null });
      },

      reset: () => {
        set({
          profile: null,
          stats: null,
          activity: [],
          transactions: [],
          ratings: [],
          givenRatings: [],
          settings: null,
          verificationStatus: null,
          preferences: {
            notifications: {
              email: true,
              sms: false,
              push: true,
            },
            privacy: {
              profileVisibility: 'public',
              showEmail: false,
              showPhone: false,
            },
            language: 'en',
            currency: 'XAF',
          },
          loading: false,
          error: null,
        });
      },
    }),
    {
      name: 'user-storage',
      partialize: (state) => ({
        profile: state.profile,
        preferences: state.preferences,
        settings: state.settings,
        // Don't persist activity, transactions, ratings - fetch fresh
      }),
    }
  )
);

// Memoized Selectors
export const selectUserStats = createSelector(
  (state) => {
    const { profile, stats, ratings, activity } = state;
    return {
      ...stats,
      totalRatings: ratings?.length || 0,
      averageRating: ratings?.length
        ? ratings.reduce((sum, r) => sum + (r.rating || 0), 0) / ratings.length
        : 0,
      totalActivity: activity?.length || 0,
      isVerified: profile?.verificationLevel === 'verified',
    };
  }
);

export const selectUserPreferences = createSelector(
  (state) => state.preferences
);

export default useUserStore;
